/**
 * Cart Totals Calculator — USA market
 *
 * UK→USA imports have no sales tax collected at the border.
 * Import duty only applies when the products total exceeds the US $800 de minimis
 * threshold (19 U.S.C. § 1321). Orders at or below $800 are duty-free.
 *
 * Admin fee conversion: admin_fee_gbp × Shopify.currency.rate (GBP→USD).
 *
 * All element IDs use the suffix "-usa" (not "-us") to avoid collisions with
 * the shared vat-excl liquid which also renders on this page using suffix "us"
 * (derived from destination|downcase on the US Shopify market).
 */

document.addEventListener('DOMContentLoaded', () => {
  const _log =
    typeof window.createLogger === 'function'
      ? window.createLogger('CartTotalsCalculatorUSA')
      : { log: () => {}, warn: () => {}, error: () => {} };

  _log.log('DOMContentLoaded fired');

  if (!window.CartItemsDataUSA) {
    _log.warn('CartItemsDataUSA not found');
    return;
  }

  let cartData = window.CartItemsDataUSA;
  const shippingCountries = cartData.shippingCountries;
  const dutyData = cartData.dutyData;
  const currency = cartData.currency || 'USD';

  let currentShippingRateCents = 0;

  // US market prices are already tax-exclusive.
  let cartTotalExclTaxCents = cartData.cartTotalWithVAT;

  // $800 USD de minimis threshold — no import duty on orders at or below this amount.
  const DE_MINIMIS_CENTS = 0;

  _log.log('Initialized — cart total (cents):', cartTotalExclTaxCents);

  // =====================================================
  // Admin fee: admin_fee_gbp × GBP→USD rate
  // =====================================================
  function getAdminFeeCents() {
    if (!shippingCountries || !shippingCountries['US']) return 0;
    const countryData = shippingCountries['US'];
    const adminFeeGBP = parseFloat(countryData.admin_fee_gbp) || 0;
    if (!adminFeeGBP) return 0;
    const gbpToUsd = parseFloat(window.Shopify?.currency?.rate) || 1;
    const adminFeeUSDCents = adminFeeGBP * 100 * gbpToUsd;
    _log.log('Admin fee — GBP:', adminFeeGBP, '| rate:', gbpToUsd, '| USD cents:', adminFeeUSDCents);
    return adminFeeUSDCents;
  }

  function getDutyRate(countryOfOrigin, destination, hsCode) {
    if (!dutyData || !countryOfOrigin || !destination || !hsCode) return 1.0;
    const raw = dutyData[countryOfOrigin]?.[destination]?.[hsCode];
    if (raw === undefined || raw === null) return 1.0;
    return raw === 1 ? 1.0 : raw / 100.0 + 1.0;
  }

  // =====================================================
  // Money formatting — always en-US / USD → $362.36
  // =====================================================
  function fmt(amountCents) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(amountCents, { locale: 'en-US', currency, isCents: true });
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amountCents / 100);
  }

  function updateElement(id, value, isHTML = false) {
    const el = document.getElementById(id);
    if (!el) return;
    if (isHTML) el.innerHTML = value;
    else el.textContent = value;
  }

  function toggleElementVisibility(id, shouldShow) {
    const el = document.getElementById(id);
    if (el) el.style.display = shouldShow ? '' : 'none';
  }

  // =====================================================
  // Core calculation
  // =====================================================
  function calculate() {
    try {
      const adminFeeCents = getAdminFeeCents();

      let totalItemsDutyCents = 0.0;
      let shippingDutyCents = 0.0;

      // Duty only applies when products total exceeds the US $800 de minimis threshold.
      if (cartTotalExclTaxCents > DE_MINIMIS_CENTS) {
        let cartTotalDutyWeitage = 0.0;

        cartData.items.forEach((item) => {
          let origin = item.countryOfOrigin;
          let hsCode = item.hsCode;

          if (window.CartApiStockData?.products && item.sku) {
            const apiData = window.CartApiStockData.products[item.sku];
            if (apiData) {
              if (apiData.country_of_origin) origin = apiData.country_of_origin;
              if (apiData.hs_code) hsCode = apiData.hs_code;
            }
          }

          const dutyRate = getDutyRate(origin, 'US', hsCode);
          const itemPriceExclCents = item.finalLinePrice;
          const itemPriceWithDutyCents = itemPriceExclCents * dutyRate;

          totalItemsDutyCents += itemPriceWithDutyCents - itemPriceExclCents;
          cartTotalDutyWeitage += (itemPriceExclCents / cartTotalExclTaxCents) * dutyRate;
        });

        const shippingPlusDutyCents = currentShippingRateCents * cartTotalDutyWeitage;
        shippingDutyCents = shippingPlusDutyCents - currentShippingRateCents;
      }

      const totalDutyCents = totalItemsDutyCents + shippingDutyCents;
      const effectiveShippingCents = currentShippingRateCents + shippingDutyCents;
      const allTotalCents = cartTotalExclTaxCents + totalItemsDutyCents + effectiveShippingCents + adminFeeCents;

      _log.log('Calculation:', {
        cartTotalExclTaxCents,
        aboveDeMinimis: cartTotalExclTaxCents > DE_MINIMIS_CENTS,
        totalItemsDutyCents,
        shippingDutyCents,
        adminFeeCents,
        allTotalCents
      });

      updateElement('cart-total-without-vat-usa', fmt(cartTotalExclTaxCents));
      updateElement('duty-items-container-usa', '', true);
      updateElement('total-items-duty-amount-display-usa', fmt(totalItemsDutyCents));
      updateElement('shipping-rate-display-usa', fmt(currentShippingRateCents));
      toggleElementVisibility('shipping-summary-usa', currentShippingRateCents > 0);
      updateElement('shipping-duty-amount-display-usa', fmt(shippingDutyCents));
      toggleElementVisibility('shipping-duty-summary-usa', shippingDutyCents > 0);
      updateElement('admin-fee-amount-display-usa', fmt(adminFeeCents));
      updateElement('subtotal-excl-tax-amount-display-usa', fmt(allTotalCents));
      updateElement('pay-to-us-amount-display-usa', fmt(cartTotalExclTaxCents + currentShippingRateCents));
      updateElement('pay-to-courier-amount-display-usa', fmt(totalDutyCents + adminFeeCents));

      const dutyLabel = document.getElementById('duty-label-usa');
      if (dutyLabel) dutyLabel.style.display = totalItemsDutyCents > 0 ? 'inline' : 'none';

      showAlwaysVisibleSections();
    } catch (err) {
      _log.error('Calculation error:', err);
    }
  }

  function showAlwaysVisibleSections() {
    const alwaysVisible = [
      'how-you-pay-section-usa',
      'vat-excl-non-ioss-footer-usa',
      'admin-fee-summary-usa',
      'admin-fee-summary-divider-usa',
      'subtotal-excl-tax-summary-usa',
      'pay-to-us-summary-usa',
      'pay-to-courier-amount-summary-usa'
    ];
    alwaysVisible.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.style.display = '';
    });
  }

  function updateCartData(shopifyCart) {
    cartTotalExclTaxCents = shopifyCart.total_price || 0;
    _log.log('Cart data updated — total cents:', cartTotalExclTaxCents);
  }

  // =====================================================
  // Event listeners
  // =====================================================
  window.addEventListener('US-shipping-country-selected', (event) => {
    try {
      _log.log('Country selected:', event.detail.code);
      currentShippingRateCents = 0;
      calculate();
    } catch (err) {
      _log.warn('Country selection error:', err);
    }
  });

  window.addEventListener('US-shipping-rate-selected', (event) => {
    try {
      const shippingRate = event.detail.shipping.price * 100;
      _log.log('Shipping rate selected:', shippingRate);
      currentShippingRateCents = shippingRate;
      calculate();
    } catch (err) {
      _log.warn('Shipping rate selection error:', err);
    }
  });

  window.addEventListener('cart-forklift-processed', (event) => {
    try {
      if (event.detail?.cartData) {
        updateCartData(event.detail.cartData);
        calculate();
      }
    } catch (err) {
      _log.warn('Cart forklift processed error:', err);
    }
  });

  window.addEventListener('cart-api-stock-data-ready', () => {
    try {
      calculate();
    } catch (err) {
      _log.warn('API stock data event error:', err);
    }
  });

  // =====================================================
  // Initial render + visibility observer
  // =====================================================
  calculate();

  const container = document.getElementById('usa-totals-cart-summary');
  if (container) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          if (container.style.display !== 'none') {
            _log.log('Container became visible, recalculating...');
            calculate();
          }
        }
      });
    });
    observer.observe(container, { attributes: true, attributeFilter: ['style'] });
    _log.log('Visibility observer attached');
  } else {
    _log.warn('Container #usa-totals-cart-summary not found');
  }

  _log.log('Module loaded');
});
